/*
 * Talla en el carrito — Mejoras UX Rivera (punto 3, septiembre 2026)
 *
 * El Liquid (snippets/talla-en-carrito.liquid) pinta en cada línea "Talla: M · Cambiar" y el grupo de
 * tallas. Este archivo hace el cambio sin quitar la prenda:
 *
 *  - Shopify.actions.updateCart con merchandiseId cambia la variante de la línea: se queda en su
 *    sitio, conserva sus propiedades y, si la talla ya estaba en otra línea igual, Shopify las junta
 *    (medido el 24-sep-2026).
 *  - Sin configurar, updateCart recarga la página; aquí se configura para que repinte el tema.
 *  - El tema expone su bus en window.r$1 (theme.js, código de la agencia): quick-cart:updated
 *    repinta el carrito lateral y cart:updated la página del carrito y el contador de la cabecera.
 *  - Pre-venta: se ponen o se quitan las propiedades que pone Globo desde la ficha
 *    (_is_preorder, _preorder_locale, _esid) según la talla nueva (data-preventa).
 *
 * Todo va delegado en document, porque el tema vuelve a pintar las líneas por AJAX.
 * Spec y plan: _SPECS/eduardo-rivera/003-cambiar-talla-en-carrito.
 */
(() => {
  const SEL = {
    control: '[data-talla-carrito]',
    cambiar: '[data-talla-cambiar]',
    opciones: '[data-talla-opciones]',
    talla: '[data-talla-variante]',
    error: '[data-talla-error]',
    linea: '[data-input-item]',
    botonesDelTema: '[data-add-quantity], [data-subtract-quantity], [data-remove-item]',
  };
  const CLAVES_GLOBO = ['_is_preorder', '_preorder_locale', '_esid', '_preorder_discount', '_preorder_partial_payment'];
  const TEXTO_PREVENTA = 'Artículo en Pre-Order';
  // La clase que pone el tema en una línea mientras cambia su cantidad; aquí también, mientras cambia la talla.
  const OCUPADA = 'has-quantity-update';
  // Un cambio de talla cada vez: si uno junta líneas, las posiciones de las demás cambian.
  let enCurso = false;

  const rutaCarrito = () => (window.theme && window.theme.routes && window.theme.routes.cart && window.theme.routes.cart.base) || '/cart';
  const leerCarrito = () => fetch(`${rutaCarrito()}.js`, { credentials: 'include' }).then(r => r.json());
  const lineasDe = cart => (cart && cart.lines && (cart.lines.nodes || cart.lines)) || [];

  // --- Anuncios para lectores de pantalla ------------------------------------------------------

  let vivo;
  function anunciar(texto) {
    if (!texto) return;
    if (!vivo) {
      vivo = document.createElement('div');
      vivo.className = 'talla-carrito__vivo';
      vivo.setAttribute('role', 'status');
      vivo.setAttribute('aria-live', 'polite');
      document.body.appendChild(vivo);
    }
    vivo.textContent = '';
    setTimeout(() => { vivo.textContent = texto; }, 50);
  }

  // --- Repintado del tema ----------------------------------------------------------------------

  async function repintar() {
    const bus = window.r$1;
    if (typeof bus !== 'function') {
      // Con theme.min.js el bus no está expuesto: último recurso, recargar.
      console.warn('[talla-en-carrito] window.r$1 no existe: se recarga la página');
      window.location.reload();
      return;
    }
    // /cart servida con el carrito vacío: refreshCart de Stiletto busca las líneas antes de pedirlas y
    // falla (theme.js:11834, «Cannot set properties of null»). Ahí se recarga, como hacía Shopify.
    const paginaCarrito = document.querySelector('[data-section-type="cart"]');
    if (paginaCarrito && !paginaCarrito.querySelector('[js-cart-items]')) {
      window.location.reload();
      return;
    }
    const cart = await leerCarrito().catch(() => null);
    bus('quick-cart:updated');
    if (cart) bus('cart:updated', { cart });
  }

  // --- updateCart sin recargar la página ------------------------------------------------------

  function configurar() {
    const accion = window.Shopify && window.Shopify.actions && window.Shopify.actions.updateCart;
    if (!accion || typeof accion.configure !== 'function') return false;
    if (window.__tallaEnCarritoConfigurado) return true;
    try {
      accion.configure({
        async handler(defaultHandler) {
          const resultado = await defaultHandler();
          if (!(resultado && resultado.userErrors && resultado.userErrors.length)) await repintar();
          return resultado;
        },
      });
    } catch (error) {
      console.warn('[talla-en-carrito] no se pudo configurar updateCart', error);
      return false;
    }
    window.__tallaEnCarritoConfigurado = true;
    return true;
  }
  // Shopify.actions puede llegar después que este script.
  (function intentar(n) {
    if (!configurar() && n > 0) setTimeout(() => intentar(n - 1), 250);
  })(40);

  // --- Abrir y cerrar el grupo de tallas -------------------------------------------------------

  function alternar(control, abrir) {
    const boton = control.querySelector(SEL.cambiar);
    const grupo = control.querySelector(SEL.opciones);
    if (!boton || !grupo) return;
    const abierto = typeof abrir === 'boolean' ? abrir : grupo.hidden;
    grupo.hidden = !abierto;
    boton.setAttribute('aria-expanded', String(abierto));
    if (abierto) {
      const actual = grupo.querySelector('[aria-pressed="true"]') || grupo.querySelector(SEL.talla);
      if (actual) actual.focus();
    }
  }

  // --- Errores ---------------------------------------------------------------------------------

  function mostrarError(control) {
    const error = control.querySelector(SEL.error);
    const texto = control.dataset.textoError || '';
    if (error) {
      error.textContent = texto;
      error.hidden = false;
    }
    anunciar(texto);
  }

  function ocultarError(control) {
    const error = control.querySelector(SEL.error);
    if (error) error.hidden = true;
  }

  // --- Propiedades de la línea, con o sin pre-venta -----------------------------------------------
  // Se leen de /cart.js, que las da tal cual. Las de Liquid (item.properties) llegan escapadas para HTML:
  // «<3 &» volvía al carrito como «&lt;3 &amp;» (QA del 24-sep-2026).

  // Las propiedades tal como están, para volver a la talla de antes.
  function atributosOriginales(props) {
    return Object.entries(props || {})
      .filter(([, valor]) => valor !== null && valor !== '')
      .map(([key, value]) => ({ key, value: String(value) }));
  }

  function atributos(props, preventa) {
    const lista = Object.entries(props || {})
      .filter(([clave, valor]) => !CLAVES_GLOBO.includes(clave) && valor !== null && valor !== '')
      .map(([key, value]) => ({ key, value: String(value) }));

    if (preventa) {
      const globo = window.Globo && window.Globo.Preorder;
      const texto = (globo && globo.translation && globo.translation.preorderedItemlabel) || TEXTO_PREVENTA;
      const locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'es';
      const esid = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
      lista.push({ key: '_is_preorder', value: texto }, { key: '_preorder_locale', value: locale }, { key: '_esid', value: esid });
    }
    return lista;
  }

  // --- Juntar líneas de la misma talla -----------------------------------------------------------
  // Shopify las junta solo si las propiedades son iguales; Globo pone a veces un _esid en compras
  // normales, así que se juntan aquí las que solo se diferencian en eso. Las de pre-venta, no.

  async function juntarRepetidas(variante) {
    const datos = await leerCarrito();
    const sinEsid = props =>
      JSON.stringify(Object.entries(props || {}).filter(([clave]) => clave !== '_esid').sort());
    const candidatas = datos.items
      .map((item, indice) => ({ item, indice }))
      .filter(({ item }) => String(item.variant_id) === String(variante) && !(item.properties && item.properties._is_preorder));
    if (candidatas.length < 2) return;

    const base = sinEsid(candidatas[0].item.properties);
    const grupo = candidatas.filter(({ item }) => sinEsid(item.properties) === base);
    if (grupo.length < 2) return;

    const { cart } = await window.Shopify.actions.getCart();
    const lineas = lineasDe(cart);
    if (grupo.some(({ item, indice }) => !lineas[indice] || lineas[indice].quantity !== item.quantity)) return;

    const total = grupo.reduce((suma, { item }) => suma + item.quantity, 0);
    await window.Shopify.actions.updateCart({
      lines: grupo.map(({ indice }, n) => ({ id: lineas[indice].id, quantity: n === 0 ? total : 0 })),
    });
  }

  // --- Foco (y, si hace falta, un error) en la línea repintada ---------------------------------------

  // El tema puede repintar más de una vez (cada updateCart repinta): se sigue mirando hasta que la línea
  // lleva 1,5 s sin cambiar y, cada vez que se repinta, se le vuelve a poner el foco y el error.
  // zona se calcula antes del cambio: cuando el tema repinta, la línea vieja sale del documento y
  // closest() ya no encuentra su carrito.
  const zonaDe = control => (control.closest('.quick-cart') ? '.quick-cart' : '[data-section-type="cart"]');

  function enfocarDespues(zona, control, variante, textoError) {
    const inicio = Date.now();
    let ultimo = null;
    let cambio = 0;
    let anunciado = false;
    (function buscar() {
      const nuevo = document.querySelector(`${zona} ${SEL.control}[data-variante="${variante}"]`);
      if (nuevo && nuevo !== control && nuevo !== ultimo) {
        ultimo = nuevo;
        cambio = Date.now();
        if (textoError) {
          const error = nuevo.querySelector(SEL.error);
          if (error) {
            error.textContent = textoError;
            error.hidden = false;
          }
          if (!anunciado) anunciar(textoError);
          anunciado = true;
        }
        const boton = nuevo.querySelector(SEL.cambiar);
        if (boton) boton.focus();
        const globo = window.Globo && window.Globo.Preorder;
        if (zona !== '.quick-cart' && globo && typeof globo.handleLoadRenderCartPage === 'function') {
          try { globo.handleLoadRenderCartPage(); } catch (e) { /* lo de Globo no debe romper el carrito */ }
        }
      }
      const quieta = ultimo && Date.now() - cambio >= 1500;
      if (!quieta && Date.now() - inicio < 6000) setTimeout(buscar, 100);
    })();
  }

  // --- Volver a la talla de antes ------------------------------------------------------------------
  // Si en la talla nueva no hay stock para la cantidad de la línea, Shopify no rechaza el cambio: recorta
  // la cantidad (medido el 24-sep-2026, S×20 → XS×15). Se deshace para no quitar unidades sin avisar.

  async function deshacer(control, indice, cantidad, props) {
    const { cart } = await window.Shopify.actions.getCart();
    const objetivo = lineasDe(cart)[indice];
    if (!objetivo) throw new Error('no se encuentra la línea para deshacer');
    await window.Shopify.actions.updateCart({
      lines: [{
        id: objetivo.id,
        merchandiseId: `gid://shopify/ProductVariant/${control.dataset.variante}`,
        quantity: cantidad,
        attributes: atributosOriginales(props),
      }],
    });
  }

  // --- El cambio -------------------------------------------------------------------------------

  async function cambiar(control, boton) {
    const key = control.dataset.key;
    const linea = control.closest(SEL.linea);
    if (enCurso) return;
    if (boton.getAttribute('aria-disabled') === 'true' || boton.getAttribute('aria-pressed') === 'true') return;
    // El tema está cambiando la cantidad de esta línea (+/−): hasta que la repinte, la cantidad del
    // DOM y la del carrito no coinciden y el cambio se comería el +/− (QA del 24-sep-2026).
    if (linea && linea.classList.contains(OCUPADA)) return;

    const zona = zonaDe(control);
    ocultarError(control);

    // Sin unidades para la cantidad de la línea (data-cabe, del Liquid): aviso al momento, sin cambiar.
    if (boton.dataset.cabe === 'false') {
      const error = control.querySelector(SEL.error);
      const texto = (control.dataset.textoStock || '').replace('__TALLA__', boton.dataset.tallaValor || '');
      if (error) {
        error.textContent = texto;
        error.hidden = false;
      }
      anunciar(texto);
      return;
    }

    if (!configurar()) {
      mostrarError(control);
      return;
    }

    enCurso = true;
    if (linea) linea.classList.add(OCUPADA);
    control.setAttribute('aria-busy', 'true');

    const variante = boton.dataset.tallaVariante;
    const indice = Number(control.dataset.linea) - 1;
    try {
      // La línea se identifica por su key en /cart.js (misma posición que en el Liquid) y la cantidad se
      // toma de ahí, no del DOM, que puede ir por detrás.
      const actual = (await leerCarrito()).items[indice];
      if (!actual || actual.key !== key) throw new Error('la línea ya no está en esa posición');
      const cantidad = actual.quantity;
      const props = actual.properties || {};

      const { cart } = await window.Shopify.actions.getCart();
      const objetivo = lineasDe(cart)[indice];
      // Las líneas de getCart van en el mismo orden que /cart.js: se comprueba la cantidad.
      if (!objetivo || objetivo.quantity !== cantidad) throw new Error('la línea no coincide con el carrito');

      const resultado = await window.Shopify.actions.updateCart({
        lines: [{
          id: objetivo.id,
          merchandiseId: `gid://shopify/ProductVariant/${variante}`,
          quantity: cantidad,
          attributes: atributos(props, boton.dataset.preventa === 'true'),
        }],
      });
      if (resultado && resultado.userErrors && resultado.userErrors.length) {
        throw new Error(resultado.userErrors[0].message || 'userErrors');
      }

      const tras = (await leerCarrito()).items[indice];
      if (tras && String(tras.variant_id) === String(variante) && tras.quantity < cantidad) {
        await deshacer(control, indice, cantidad, props);
        enfocarDespues(zona, control, control.dataset.variante, (control.dataset.textoStock || '').replace('__TALLA__', boton.dataset.tallaValor || ''));
        return;
      }

      await juntarRepetidas(variante).catch(error => console.warn('[talla-en-carrito] juntar', error));
      const cambiada = (control.dataset.textoCambiada || '').replace('__TALLA__', boton.dataset.tallaValor || '');
      anunciar(boton.dataset.preventa === 'true' ? `${cambiada} · ${control.dataset.textoPreventa || ''}` : cambiada);
      enfocarDespues(zona, control, variante);
    } catch (error) {
      console.warn('[talla-en-carrito]', error);
      mostrarError(control);
    } finally {
      enCurso = false;
      if (linea) linea.classList.remove(OCUPADA);
      control.removeAttribute('aria-busy');
    }
  }

  // --- Clics ---------------------------------------------------------------------------------------
  // En captura: mientras se cambia una talla, los botones de cantidad, "Eliminar" y las tallas de todas
  // las líneas no hacen nada (un cambio que junta líneas mueve las posiciones de las demás).

  document.addEventListener('click', event => {
    if (enCurso && event.target.closest(`${SEL.botonesDelTema}, ${SEL.talla}`)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const botonCambiar = event.target.closest(SEL.cambiar);
    if (botonCambiar) {
      event.preventDefault();
      alternar(botonCambiar.closest(SEL.control));
      return;
    }

    const talla = event.target.closest(SEL.talla);
    if (talla) {
      event.preventDefault();
      cambiar(talla.closest(SEL.control), talla);
    }
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const control = event.target.closest && event.target.closest(SEL.control);
    const grupo = control && control.querySelector(SEL.opciones);
    if (grupo && !grupo.hidden) {
      alternar(control, false);
      control.querySelector(SEL.cambiar).focus();
    }
  });
})();
